import { useEffect, useRef, type CSSProperties } from "react";

interface Point {
  x: number;
  y: number;
}

interface Boundary {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

interface Move {
  controls: [Point, Point];
  duration: number;
  end: Point;
  start: Point;
}

const cursors = [
  { color: "#7c3aed", name: "Alex" },
  { color: "#0284c7", name: "Maya" },
  { color: "#059669", name: "Sam" },
  { color: "#db2777", name: "Alyssa" },
] as const;

const CURSOR_WIDTH = 84;
const CURSOR_HEIGHT = 36;

function randomBetween(minimum: number, maximum: number) {
  return minimum + Math.random() * (maximum - minimum);
}

function cubic(move: Move, progress: number) {
  const inverse = 1 - progress;
  return {
    x:
      move.start.x * inverse ** 3 +
      move.controls[0].x * 3 * inverse ** 2 * progress +
      move.controls[1].x * 3 * inverse * progress ** 2 +
      move.end.x * progress ** 3,
    y:
      move.start.y * inverse ** 3 +
      move.controls[0].y * 3 * inverse ** 2 * progress +
      move.controls[1].y * 3 * inverse * progress ** 2 +
      move.end.y * progress ** 3,
  };
}

function getTextBoundaries(hero: HTMLElement): Boundary[] {
  const heroRect = hero.getBoundingClientRect();
  const boundaries: Boundary[] = [];

  hero
    .querySelectorAll<HTMLElement>(".home-brand, .home-copy")
    .forEach((root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();

      while (node) {
        if (node.textContent?.trim()) {
          const range = document.createRange();
          range.selectNodeContents(node);

          Array.from(range.getClientRects()).forEach((rect) => {
            const paddingX = 20 + rect.width * 0.1;
            const paddingY = 16 + rect.height * 0.1;
            boundaries.push({
              bottom:
                ((rect.bottom + paddingY - heroRect.top) / heroRect.height) *
                100,
              left:
                ((rect.left - CURSOR_WIDTH - paddingX - heroRect.left) /
                  heroRect.width) *
                100,
              right:
                ((rect.right + paddingX - heroRect.left) / heroRect.width) *
                100,
              top:
                ((rect.top - CURSOR_HEIGHT - paddingY - heroRect.top) /
                  heroRect.height) *
                100,
            });
          });
        }
        node = walker.nextNode();
      }
    });

  return boundaries;
}

function isSafe(point: Point, boundaries: Boundary[]) {
  return !boundaries.some(
    (boundary) =>
      point.x > boundary.left &&
      point.x < boundary.right &&
      point.y > boundary.top &&
      point.y < boundary.bottom,
  );
}

function randomSafePoint(boundaries: Boundary[]) {
  for (let attempt = 0; attempt < 200; attempt += 1) {
    const point = {
      x: randomBetween(5, 88),
      y: randomBetween(7, 90),
    };
    if (isSafe(point, boundaries)) return point;
  }

  throw new Error("Hero has no safe cursor position");
}

function createMove(start: Point, boundaries: Boundary[]): Move {
  while (true) {
    const end = randomSafePoint(boundaries);
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const distance = Math.hypot(deltaX, deltaY);
    if (distance < 14) continue;

    const bend = randomBetween(-0.2, 0.2);
    const perpendicularX = (-deltaY / distance) * distance * bend;
    const perpendicularY = (deltaX / distance) * distance * bend;
    return {
      controls: [
        {
          x: start.x + deltaX * 0.33 + perpendicularX,
          y: start.y + deltaY * 0.33 + perpendicularY,
        },
        {
          x: start.x + deltaX * 0.67 + perpendicularX,
          y: start.y + deltaY * 0.67 + perpendicularY,
        },
      ],
      duration: randomBetween(700, 1100),
      end,
      start,
    };
  }
}

function HeroCursor({
  color,
  delay,
  name,
}: {
  color: string;
  delay: number;
  name: string;
}) {
  const cursorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    const layer = cursor?.parentElement;
    const hero = layer?.parentElement;
    if (!cursor || !layer || !hero) return;

    let boundaries = getTextBoundaries(hero);
    let position = randomSafePoint(boundaries);
    let move: Move | null = null;
    let moveStarted = 0;
    let progress = 0;
    let progressVelocity = 0;
    let previousTime = performance.now();
    let restingUntil = previousTime + delay;
    let frame = 0;

    const render = () => {
      cursor.style.transform = `translate3d(${(position.x / 100) * layer.clientWidth}px, ${(position.y / 100) * layer.clientHeight}px, 0)`;
    };

    const resizeObserver = new ResizeObserver(() => {
      boundaries = getTextBoundaries(hero);
      if (isSafe(position, boundaries)) return;

      position = randomSafePoint(boundaries);
      move = null;
      restingUntil = performance.now() + randomBetween(4000, 6000);
      render();
    });
    resizeObserver.observe(hero);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      render();
      return () => resizeObserver.disconnect();
    }

    const animate = (time: number) => {
      const delta = Math.min((time - previousTime) / 1000, 0.032);
      previousTime = time;

      if (!move && time >= restingUntil) {
        move = createMove(position, boundaries);
        moveStarted = time;
        progress = 0;
        progressVelocity = 0;
      }

      if (move) {
        const target = Math.min((time - moveStarted) / move.duration, 1);
        progressVelocity +=
          ((target - progress) * 110 - progressVelocity * 20) * delta;
        progress += progressVelocity * delta;
        position = cubic(move, Math.min(Math.max(progress, 0), 1));

        if (target === 1 && Math.abs(1 - progress) < 0.002) {
          position = move.end;
          move = null;
          restingUntil = time + randomBetween(4000, 6000);
        }
      }

      render();
      frame = requestAnimationFrame(animate);
    };

    render();
    frame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, [delay]);

  return (
    <span
      className="hero-cursor"
      ref={cursorRef}
      style={{ "--cursor-color": color } as CSSProperties}
    >
      <svg viewBox="0 0 24 30">
        <path d="M2 2 20 17l-8 1 4 8-4 2-4-8-6 6Z" />
      </svg>
      <span>{name}</span>
    </span>
  );
}

export function HeroCursors() {
  return (
    <div className="hero-cursors" aria-hidden="true">
      {cursors.map((cursor, index) => (
        <HeroCursor {...cursor} delay={index * 320} key={cursor.name} />
      ))}
    </div>
  );
}
